import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PropertyActions } from './property.actions';
import {
  catchError,
  from,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { PropertyService } from '@core/services/property.service';
import { HelperService } from '@core/helper/helper.service';
import { PropertyExportQueuedResponse } from '@core/interface/property';
import { HttpResponse } from '@angular/common/http';
import saveAs from 'file-saver';
import moment from 'moment';

@Injectable()
export class PropertyEffects {
  private actions$ = inject(Actions);
  private propertyService = inject(PropertyService);
  private translateService = inject(TranslateService);
  private helperService = inject(HelperService);

  getProperties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PropertyActions.loadProperties),
      mergeMap((data) =>
        this.propertyService.getProperties(data).pipe(
          map((properties) =>
            properties.success
              ? PropertyActions.loadPropertiesSuccess(properties)
              : PropertyActions.loadPropertiesFailure(properties)
          ),
          catchError((error) =>
            of(
              PropertyActions.loadPropertiesFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  addProperty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PropertyActions.addEditProperty),
      mergeMap((data) =>
        this.propertyService.addProperty(data).pipe(
          map((property) => {
            if (property.success) {
              return data.id
                ? PropertyActions.updatePropertySuccess(property)
                : PropertyActions.addPropertySuccess(property);
            } else return PropertyActions.addPropertyFailure(property);
          }),
          catchError((error) =>
            of(
              PropertyActions.addPropertyFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  deleteProperty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PropertyActions.deleteProperty),
      mergeMap((data) =>
        this.propertyService.deleteProperty(data.id).pipe(
          map((response) =>
            response.success
              ? PropertyActions.deletePropertySuccess({
                  ...response,
                  id: data.id,
                })
              : PropertyActions.deletePropertyFailure(response)
          ),
          catchError((error) =>
            of(
              PropertyActions.deletePropertyFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  /**
   * Sync (<=5000): 200 + xlsx blob → download immediately.
   * Async (>5000 / async=1): 202 JSON → show email message (no browser download).
   * Never treat HTTP 202 as an error.
   */
  exportExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PropertyActions.exportExcelProperties),
      mergeMap(({ type: _actionType, ...filter }) =>
        this.propertyService.exportProperties(filter).pipe(
          switchMap((response) => {
            const blob = response.body;
            if (!blob || blob.size === 0) {
              return of(
                PropertyActions.exportExcelPropertiesFailure({
                  success: false,
                  message: this.translateService.instant(
                    'There is no data to export.'
                  ),
                })
              );
            }

            // Large export queued — JSON body (may arrive as blob with responseType: 'blob')
            if (this.isQueuedJsonResponse(response)) {
              return from(blob.text()).pipe(
                switchMap((text) => this.handleQueuedExport(text))
              );
            }

            // Small export — only save when response is an actual spreadsheet file
            if (!this.isSpreadsheetFileResponse(response)) {
              return from(blob.text()).pipe(
                map((text) => {
                  let message = this.translateService.instant(
                    'Something went wrong'
                  );
                  try {
                    const json = JSON.parse(text);
                    if (json?.message) {
                      message = json.message;
                    }
                  } catch {
                    // keep default
                  }
                  return PropertyActions.exportExcelPropertiesFailure({
                    success: false,
                    message,
                  });
                })
              );
            }

            const fileName =
              this.getFilenameFromContentDisposition(
                response.headers.get('Content-Disposition')
              ) ||
              `properties_export_${moment().format('YYYY_MM_DD_HH_mm_ss')}.xlsx`;

            saveAs(blob, fileName);
            return of(PropertyActions.exportExcelPropertiesSuccess({}));
          }),
          catchError((error) =>
            of(
              PropertyActions.exportExcelPropertiesFailure({
                error,
                success: false,
                message:
                  typeof error === 'string'
                    ? error
                    : error?.message ||
                      this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  exportExcelSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PropertyActions.exportExcelPropertiesSuccess),
        tap(({ message, emailed }) => {
          if (emailed) {
            this.helperService.showInfoToastr(
              message ||
                this.translateService.instant(
                  'Your export has more than 5,000 records. It is being generated and will be emailed to you when ready.'
                )
            );
            return;
          }

          this.helperService.showSuccessToastr(
            message ||
              this.translateService.instant('Properties exported successfully')
          );
        })
      ),
    { dispatch: false }
  );

  exportExcelFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PropertyActions.exportExcelPropertiesFailure),
        tap(({ message, error }) => {
          // HTTP 4xx/5xx already toasted by ErrorInterceptor.
          if (!error && message) {
            this.helperService.showErrorToastr(message);
          }
        })
      ),
    { dispatch: false }
  );

  /** Large export: email only — do not poll or auto-download. */
  private handleQueuedExport(text: string) {
    let queued: PropertyExportQueuedResponse;
    try {
      queued = JSON.parse(text);
    } catch {
      return of(
        PropertyActions.exportExcelPropertiesFailure({
          success: false,
          message: this.translateService.instant('Something went wrong'),
        })
      );
    }

    if (!queued?.success && !queued?.queued) {
      return of(
        PropertyActions.exportExcelPropertiesFailure({
          success: false,
          message:
            queued?.message ||
            this.translateService.instant('Something went wrong'),
        })
      );
    }

    return of(
      PropertyActions.exportExcelPropertiesSuccess({
        emailed: true,
        message:
          queued.message ||
          this.translateService.instant(
            'Your export has more than 5,000 records. It is being generated and will be emailed to you when ready.'
          ),
      })
    );
  }

  private isQueuedJsonResponse(response: HttpResponse<Blob>): boolean {
    if (response.status === 202) {
      return true;
    }

    const contentType = (
      response.headers.get('Content-Type') ||
      response.body?.type ||
      ''
    ).toLowerCase();

    return contentType.includes('application/json');
  }

  private isSpreadsheetFileResponse(response: HttpResponse<Blob>): boolean {
    const contentType = (
      response.headers.get('Content-Type') ||
      response.body?.type ||
      ''
    ).toLowerCase();

    return (
      contentType.includes('spreadsheetml') ||
      contentType.includes('application/vnd.ms-excel') ||
      contentType.includes('application/octet-stream') ||
      contentType.includes('text/csv') ||
      contentType.includes('application/csv')
    );
  }

  private getFilenameFromContentDisposition(
    header: string | null
  ): string | null {
    if (!header) {
      return null;
    }

    const utf8Match = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1].trim().replace(/['"]/g, ''));
    }

    const match = /filename\s*=\s*("?)([^";]+)\1/i.exec(header);
    return match?.[2]?.trim() || null;
  }
}
